import React, { useState } from 'react';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { ordersApi, scansApi, employeesApi, bundlesApi } from '../services/api';
import { OrderDetailsModal } from '../components/OrderDetailsModal';
import { useEmployee } from '../contexts/EmployeeContext';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon, TruckIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

interface OrderItem {
  id: string;
  title: string;
  sku?: string;
  quantity: number;
  requires_packing_verification?: boolean;
  scanned_quantity?: number;
  verified?: boolean;
  is_bundle_component?: boolean;
  bundle_parent_sku?: string;
  barcode?: string;
}

export const ShippingStation: React.FC = () => {
  const { employee } = useEmployee();
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [originalOrderItems, setOriginalOrderItems] = useState<OrderItem[]>([]); // Store original items before bundle expansion
  const [itemsToVerify, setItemsToVerify] = useState<OrderItem[]>([]);
  const [packedItems, setPackedItems] = useState<OrderItem[]>([]);
  const [scanStep, setScanStep] = useState<'order' | 'items' | 'complete'>('order');
  const [lastScan, setLastScan] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showShipModal, setShowShipModal] = useState<boolean>(false);
  const [stationName] = useState<string>(
    localStorage.getItem('shipping_station_name') || `Ship-Station-${Math.random().toString(36).substr(2, 6)}`
  );
  const [scannerFocusKey, setScannerFocusKey] = useState<number>(0);

  const logActivity = async (orderId: string, actionType: string, description: string, itemId?: string, quantity?: number) => {
    if (!employee) return;
    try {
      await employeesApi.logActivity({
        employee_id: employee.id,
        order_id: orderId,
        action_type: actionType,
        description,
        item_id: itemId,
        quantity,
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  };

  const formatOrderNumber = (orderNumber: string | number | undefined | null) => {
    const raw = (orderNumber || '').toString().trim();
    if (!raw) return '';
    if (raw.toUpperCase().startsWith('PSCS#')) return raw.replace(/^PSCS/i, '');
    if (raw.startsWith('#') || raw.toUpperCase().startsWith('PSCS')) return raw;
    if (/^[0-9]+$/.test(raw)) return `#PSCS${raw}`;
    return `#${raw}`;
  };

  const handleBarcodeScan = async (barcode: string) => {
    setLastScan(barcode);
    setError('');

    if (scanStep === 'order') {
      await loadOrderByBarcode(barcode);
    } else if (scanStep === 'items') {
      await verifyItem(barcode);
    }
  };

  const loadOrderByBarcode = async (barcode: string) => {
    try {
      const orderNumber = barcode.trim();

      let order: any;
      try {
        const lookupResult = await ordersApi.lookup(orderNumber);
        order = lookupResult.order;
      } catch (lookupErr: any) {
        if (lookupErr?.response?.status === 404) {
          setError(`Order not found: ${orderNumber}`);
        } else {
          setError(`Failed to look up order: ${orderNumber}`);
        }
        return;
      }

      if (order.status !== 'awaiting_shipment') {
        setError(`Order ${orderNumber} has status "${order.status}" - must be "Awaiting Shipment"`);
        return;
      }

      const orderDetails = await ordersApi.get(order.id);
      setCurrentOrder(orderDetails.order);
      
      // Store original order items (before bundle expansion) for the shipping modal
      setOriginalOrderItems(orderDetails.items);
      
      logActivity(order.id, 'order_scanned', `Scanned order #${order.order_number} at shipping station`);

      let shippingScans: any = {};
      let packingScans: any = {};
      try {
        const scansData = await scansApi.getOrderScans(order.id);
        scansData.scans.forEach((scan: any) => {
          const dept = (scan.department || '').toString();
          if (dept === 'shipping') {
            shippingScans[scan.order_item_id] = (shippingScans[scan.order_item_id] || 0) + 1;
          } else if (dept === 'packing' || dept.startsWith('Station-')) {
            // Legacy compatibility: older packing station recorded department as stationName.
            packingScans[scan.order_item_id] = (packingScans[scan.order_item_id] || 0) + 1;
          }
        });
      } catch (error) {}

      const packingItems = orderDetails.items.filter((item: any) => item.requires_packing_verification);
      const actuallyPacked = packingItems.filter((item: any) => (packingScans[item.id] || 0) >= item.quantity);
      const notYetPacked = packingItems.filter((item: any) => (packingScans[item.id] || 0) < item.quantity);
      let needsVerification = orderDetails.items.filter((item: any) => !item.requires_packing_verification);

      try {
        const expandResult = await bundlesApi.expandForPacking(needsVerification);
        if (expandResult.expanded) {
          needsVerification = expandResult.items;
        }
      } catch (bundleError) {
        console.error('[ShippingStation] Bundle expansion error:', bundleError);
      }

      setPackedItems(actuallyPacked.map((item: any) => ({ ...item, verified: true })));

      const hasUnpackedRequiredItems = notYetPacked.length > 0;
      if (notYetPacked.length > 0) {
        const names = notYetPacked.map((i: any) => i.title).join(', ');
        setError(`⚠️ ${notYetPacked.length} item(s) not yet verified at packing station: ${names}`);
      }

      const itemsWithScans = needsVerification.map((item: any) => ({
        ...item,
        scanned_quantity: shippingScans[item.id] || 0,
        verified: (shippingScans[item.id] || 0) >= item.quantity,
      }));

      setItemsToVerify(itemsWithScans);

      const allShippingVerified = itemsWithScans.every((i: any) => i.verified);

      // Never allow "complete" state if required packing scans are missing.
      if (hasUnpackedRequiredItems) {
        setScanStep('items');
      } else if (allShippingVerified && itemsWithScans.length > 0) {
        setScanStep('complete');
      } else {
        setScanStep(needsVerification.length > 0 ? 'items' : 'complete');
      }

      playSuccessBeep();
    } catch (err: any) {
      setError(`Failed to load order: ${err.message}`);
    }
  };

  const verifyItem = async (barcode: string) => {
    const itemIndex = itemsToVerify.findIndex(
      (item) =>
        item.sku === barcode ||
        item.barcode === barcode ||
        (item as any).variant_sku === barcode ||
        (item as any).variant_barcode === barcode
    );

    if (itemIndex === -1) {
      setError(`Item not found: ${barcode}`);
      playErrorBeep();
      return;
    }

    const updatedItems = [...itemsToVerify];
    const item = updatedItems[itemIndex];

    if ((item.scanned_quantity || 0) < item.quantity) {
      item.scanned_quantity = (item.scanned_quantity || 0) + 1;
      item.verified = item.scanned_quantity >= item.quantity;

      try {
        await scansApi.recordScan(currentOrder.id, item.id, 'shipping');
        logActivity(currentOrder.id, 'item_scanned', `Scanned ${item.title} (${item.scanned_quantity}/${item.quantity})`, item.id, 1);
      } catch (error) {}

      setItemsToVerify(updatedItems);
      playSuccessBeep();

      const allVerified = updatedItems.every(i => i.verified);
      if (allVerified) {
        setScanStep('complete');
        logActivity(currentOrder.id, 'items_verified', 'All items verified at shipping station');
      }
    } else {
      setError(`Already scanned: ${item.title}`);
      playErrorBeep();
    }
  };

  const handleVerifyItem = async (itemId: string) => {
    const itemIndex = itemsToVerify.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return;
    
    const updatedItems = [...itemsToVerify];
    const item = updatedItems[itemIndex];
    
    // Record scans for remaining quantity
    const scansNeeded = item.quantity - (item.scanned_quantity || 0);
    for (let i = 0; i < scansNeeded; i++) {
      try {
        await scansApi.recordScan(currentOrder.id, item.id, 'shipping');
      } catch (error) {
        console.error('Failed to save scan:', error);
      }
    }
    
    item.scanned_quantity = item.quantity;
    item.verified = true;
    
    setItemsToVerify(updatedItems);
    playSuccessBeep();
    
    const allVerified = updatedItems.every(i => i.verified);
    if (allVerified) {
      setScanStep('complete');
      logActivity(currentOrder.id, 'items_verified', 'All items verified at shipping station');
    }
  };

  const handleClearItem = (itemId: string) => {
    // Note: Scans remain in DB - this just resets local state for re-verification
    setItemsToVerify(itemsToVerify.map(item => 
      item.id === itemId ? { ...item, scanned_quantity: 0, verified: false } : item
    ));
  };

  const handleVerifyAll = async () => {
    // Save all scans to database
    for (const item of itemsToVerify) {
      if (!item.verified) {
        const scansNeeded = item.quantity - (item.scanned_quantity || 0);
        for (let i = 0; i < scansNeeded; i++) {
          try {
            await scansApi.recordScan(currentOrder.id, item.id, 'shipping');
          } catch (error) {
            console.error('Failed to save scan:', error);
          }
        }
      }
    }
    
    const allVerified = itemsToVerify.map(item => ({ ...item, scanned_quantity: item.quantity, verified: true }));
    setItemsToVerify(allVerified);
    logActivity(currentOrder.id, 'items_verified', 'All items verified at shipping station (bulk)');
    setScanStep('complete');
  };

  const handleClearAll = async () => {
    // Note: We don't delete scans from DB - just reset local state
    // On refresh, scans will be reloaded from DB
    setItemsToVerify(itemsToVerify.map(item => ({ ...item, scanned_quantity: 0, verified: false })));
  };

  const handleShipOrder = async () => {
    try {
      const freshOrderDetails = await ordersApi.get(currentOrder.id);
      setCurrentOrder(freshOrderDetails.order);
      const freshPackedItems = freshOrderDetails.items.filter((item: any) => item.requires_packing_verification);
      const freshItemsToVerify = freshOrderDetails.items.filter((item: any) => !item.requires_packing_verification);
      setPackedItems(freshPackedItems);
      setItemsToVerify(freshItemsToVerify);
      setShowShipModal(true);
    } catch (error) {
      setShowShipModal(true);
    }
  };

  const handleReset = () => {
    setCurrentOrder(null);
    setItemsToVerify([]);
    setPackedItems([]);
    setScanStep('order');
    setError('');
    setLastScan('');
    setScannerFocusKey((k) => k + 1); // re-mount scanner to force autofocus
  };

  const playSuccessBeep = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTUIGGS57OihUBELTKXh8bllHgU2jdXxxHkpBSh+zPLaizsIHGi+7OynWBIKU6zm8LJcHAU7k9n0xn4qBCeAy/DajDoJHm/A7u2nWRQKVLDo8LVeHQU+ltv1yIAtBSqDzvLaiTsIF2m98OqiVBEJTqvl8bVkHwU5kdny0H8rBSiBzvPajDwJH3HB7uynWhQLVbHp8rdgHgVAmtz2yoEuBSuEz/PcijwKGGu+8eujVRIJUK3m8rZmHwU7lNr0z4IsBCmCzvLbjTwKIG/D7+2oWxULVrLq87dhHwVBnN33y4IvBSyF0PPdjT0KGWzA8u2kVhMKUbDn87doIAU9lt3z0YMtBCqDz/PcjjwLInHE8O+pXBYMV7Tr9LhjIAVDnt73zIMwBi2H0fTejj4LGm7C8+6lVxQLUrHo9LlpIQU+mN701YQu');
    audio.play().catch(() => {});
  };

  const playErrorBeep = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRhIAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0Yf////8AAA==');
    audio.play().catch(() => {});
  };

  const allItemsVerified = itemsToVerify.length === 0 || itemsToVerify.every(item => item.verified);
  const remainingItems = itemsToVerify.filter(i => !i.verified).length;
  const totalItems = packedItems.length + itemsToVerify.length;

  return (
    <div className="absolute inset-0 bg-slate-100 flex flex-col overflow-hidden">
      {/* Header Bar */}
      <div className="bg-slate-800 text-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h1 className="text-lg font-semibold">Shipping Station</h1>
            <span className="text-slate-400">|</span>
            <span className="text-sm text-slate-300">{stationName}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-sm text-slate-300">Active</span>
          </div>
        </div>
      </div>

      {/* Order Info Bar */}
      {currentOrder && (
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-slate-900">
                  Order: <span className="text-blue-600">{formatOrderNumber(currentOrder.order_number)}</span>
                </span>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Ship To: {currentOrder.first_name} {currentOrder.last_name} • {currentOrder.shipping_city}, {currentOrder.shipping_state} {currentOrder.shipping_zip}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                New Order
              </button>
              {scanStep === 'complete' && (
                <button
                  onClick={handleShipOrder}
                  className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2"
                >
                  <TruckIcon className="w-5 h-5" />
                  Proceed to Shipping Window
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="p-6 flex-1 min-h-0 flex flex-col overflow-hidden relative">
        {/* Scan Input Section */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm mb-4 flex-shrink-0">
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-4 mb-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                scanStep === 'order' ? 'bg-blue-100 text-blue-700' :
                scanStep === 'items' ? 'bg-purple-100 text-purple-700' :
                'bg-green-100 text-green-700'
              }`}>
                {scanStep === 'order' ? 'Step 1: Scan Order' :
                 scanStep === 'items' ? 'Step 2: Verify Items' :
                 '✓ Ready to Ship'}
              </span>
            </div>
            <BarcodeScanner
              key={scannerFocusKey}
              onScan={handleBarcodeScan}
              placeholder={
                scanStep === 'order' ? 'Scan order barcode to start...' :
                'Scan or enter SKU/UPC to verify...'
              }
            />
          </div>

          {/* Feedback */}
          {(lastScan || error) && (
            <div className={`px-4 py-3 flex items-center gap-2 ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
              {error ? (
                <><XCircleIcon className="w-5 h-5" /> {error}</>
              ) : (
                <><CheckCircleIcon className="w-5 h-5" /> Scanned: <span className="font-mono font-medium">{lastScan}</span></>
              )}
            </div>
          )}
        </div>

        {/* Items Lists */}
        {currentOrder && (scanStep === 'items' || scanStep === 'complete') && (
          <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
            {/* Packed Items from Packing Station */}
            {packedItems.length > 0 && (
              <div className={`bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col ${
                itemsToVerify.length > 0 ? 'flex-shrink-0 max-h-48' : 'flex-1 min-h-0'
              }`}>
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-200">
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5" />
                    Pre-Packed Items ({packedItems.length})
                  </h3>
                  <p className="text-xs text-blue-700 mt-0.5">Verified at packing station</p>
                </div>
                <div className={`divide-y divide-slate-100 flex-1 min-h-0 overflow-y-auto ${scanStep === 'complete' ? 'pb-28' : ''}`}>
                  {packedItems.map((item) => (
                    <div key={item.id} className="grid grid-cols-12 gap-4 px-4 py-3 items-center bg-blue-50/30">
                      <div className="col-span-7">
                        <p className="font-medium text-slate-800">{item.title}</p>
                        <p className="text-xs text-slate-500">SKU: {item.sku || 'N/A'}</p>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="text-2xl font-bold text-slate-700">{item.quantity}</span>
                      </div>
                      <div className="col-span-2 text-center">
                        <span className="text-2xl font-bold text-blue-600">{item.quantity}</span>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center">
                          <CheckIcon className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items to Verify at Shipping */}
            {itemsToVerify.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col">
                <div className="flex-shrink-0 grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <div className="col-span-5">Product</div>
                  <div className="col-span-2 text-center">Actions</div>
                  <div className="col-span-2 text-center">Order Qty.</div>
                  <div className="col-span-2 text-center">Scanned</div>
                  <div className="col-span-1 text-center">Status</div>
                </div>

                {/* Quick Actions */}
                <div className="flex-shrink-0 grid grid-cols-12 gap-4 px-4 py-2 border-b border-slate-200 bg-slate-50/50">
                  <div className="col-span-5"></div>
                  <div className="col-span-2 flex items-center justify-center gap-3">
                    <button onClick={handleVerifyAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Verify All</button>
                    <span className="text-slate-300">|</span>
                    <button onClick={handleClearAll} className="text-xs text-slate-500 hover:text-slate-700 font-medium">Clear All</button>
                  </div>
                  <div className="col-span-5"></div>
                </div>

                <div className={`divide-y divide-slate-100 flex-1 min-h-0 overflow-y-auto ${scanStep === 'complete' ? 'pb-28' : ''}`}>
                  {itemsToVerify.map((item) => (
                    <div
                      key={item.id}
                      className={`grid grid-cols-12 gap-4 px-4 py-4 items-center transition-colors ${
                        item.verified ? 'bg-green-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="col-span-5">
                        {item.is_bundle_component && (
                          <p className="text-xs text-purple-600 font-medium mb-1">📦 Bundle: {item.bundle_parent_sku}</p>
                        )}
                        <p className="font-medium text-slate-900">{item.title}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span><span className="font-medium">SKU:</span> {item.sku || 'N/A'}</span>
                          {(item.barcode || (item as any).variant_barcode) && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-mono">
                              UPC: {item.barcode || (item as any).variant_barcode}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-span-2 flex items-center justify-center gap-3 text-sm">
                        <button onClick={() => handleVerifyItem(item.id)} className="text-blue-600 hover:text-blue-800 font-medium">Verify</button>
                        <span className="text-slate-300">|</span>
                        <button onClick={() => handleClearItem(item.id)} className="text-slate-500 hover:text-slate-700 font-medium">Clear</button>
                      </div>

                      <div className="col-span-2 text-center">
                        <span className="text-3xl font-bold text-slate-800">{item.quantity}</span>
                      </div>

                      <div className="col-span-2 text-center">
                        <span className={`text-3xl font-bold ${
                          item.verified ? 'text-green-600' : 
                          (item.scanned_quantity || 0) > 0 ? 'text-amber-600' : 'text-slate-300'
                        }`}>
                          {item.scanned_quantity || 0}
                        </span>
                      </div>

                      <div className="col-span-1 flex justify-center">
                        {item.verified ? (
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                            <CheckIcon className="w-5 h-5 text-white" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-200"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    The workflow will automatically advance once all items are verified.
                  </p>
                  <p className="text-sm font-semibold text-slate-900">
                    {remainingItems > 0 ? `${remainingItems} Remaining` : 'All Verified'}
                  </p>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Ready to Ship Banner */}
        {currentOrder && scanStep === 'complete' && (
          <div className="absolute bottom-0 left-6 right-6 z-10 bg-green-600 rounded-lg p-3.5 text-white text-center shadow-lg">
            <h3 className="text-lg font-semibold mb-1">All Items Verified!</h3>
            <p className="text-green-100 text-sm mb-1">Order {formatOrderNumber(currentOrder.order_number)} is ready to ship ({totalItems} items)</p>
            <p className="text-green-100 text-sm mb-3">Proceed directly to the shipping window.</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleReset}
                className="px-5 py-2 bg-white/20 text-white rounded-lg font-medium hover:bg-white/30"
              >
                Scan Another Order
              </button>
              <button
                onClick={handleShipOrder}
                className="px-6 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 flex items-center gap-2"
              >
                <TruckIcon className="w-5 h-5" />
                Proceed to Shipping Window
              </button>
            </div>
          </div>
        )}

        {/* No Order State */}
        {!currentOrder && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArrowPathIcon className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Ready to Scan</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Scan an order barcode (e.g., PSCS22602) to begin verification and shipping
            </p>
            <div className="bg-slate-50 rounded-lg p-6 max-w-lg mx-auto text-left">
              <h4 className="font-semibold text-slate-800 mb-3">Workflow</h4>
              <ol className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Scan order barcode to load items
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Scan remaining items to verify
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Review all verified items
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-800 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                  Proceed to the shipping window
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Ship Order Modal */}
      {showShipModal && currentOrder && (
        <OrderDetailsModal
          key={`modal-${currentOrder.id}`}
          order={{ ...currentOrder, items: originalOrderItems }}
          onClose={() => {
            console.log('[ShippingStation] Closing ship modal');
            setShowShipModal(false);
            setScannerFocusKey((k) => k + 1);
          }}
          onShip={() => {
            console.log('[ShippingStation] Ship button clicked');
            setShowShipModal(false);
            handleReset();
          }}
          initialTab="ship"
        />
      )}
    </div>
  );
};
